/**
 * Touch devices never fire hover, so the motion preview needs a different
 * trigger. Tiles register here and the one closest to the viewport centre gets
 * "spotlit" while the user scrolls — at most one at a time, so we never decode
 * several videos at once on a phone.
 */

type SpotlightListener = (active: boolean) => void;

const listeners = new Map<Element, SpotlightListener>();
const candidates = new Set<Element>();

let observer: IntersectionObserver | null = null;
let active: Element | null = null;
let frame = 0;

/** Only the middle band of the viewport counts, so the pick tracks scrolling. */
const BAND_MARGIN = '-38% 0px -38% 0px';

function media(query: string): boolean {
  return typeof window !== 'undefined' && window.matchMedia(query).matches;
}

/**
 * Hover-capable pointers already have a trigger, and reduced-motion users
 * should not get video that starts on its own.
 */
export function spotlightEnabled(): boolean {
  if (typeof window === 'undefined' || !('IntersectionObserver' in window)) return false;
  return media('(hover: none)') && !media('(prefers-reduced-motion: reduce)');
}

function setActive(next: Element | null): void {
  if (next === active) return;
  if (active) listeners.get(active)?.(false);
  active = next;
  if (active) listeners.get(active)?.(true);
}

function pick(): void {
  frame = 0;
  if (candidates.size === 0) {
    setActive(null);
    return;
  }

  const centre = window.innerHeight / 2;
  let best: Element | null = null;
  let bestDistance = Number.POSITIVE_INFINITY;

  for (const element of candidates) {
    const rect = element.getBoundingClientRect();
    const distance = Math.abs(rect.top + rect.height / 2 - centre);
    if (distance < bestDistance) {
      bestDistance = distance;
      best = element;
    }
  }

  setActive(best);
}

function schedulePick(): void {
  if (frame) return;
  frame = requestAnimationFrame(pick);
}

function ensureObserver(): IntersectionObserver {
  observer ??= new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) candidates.add(entry.target);
        else candidates.delete(entry.target);
      }
      schedulePick();
    },
    { rootMargin: BAND_MARGIN, threshold: 0 },
  );
  return observer;
}

export function observeTile(element: Element, listener: SpotlightListener): () => void {
  listeners.set(element, listener);
  ensureObserver().observe(element);

  return () => {
    observer?.unobserve(element);
    listeners.delete(element);
    candidates.delete(element);
    if (active === element) active = null;
    schedulePick();
  };
}
